<script setup lang="ts">
import { Modal } from '@/shared/ui/Modal'
import { Alert } from '@/shared/ui/Alert'
import { Form, FormErrorAlerts } from '@/shared/ui/Form'
import { CancelButton, SaveButton } from '@/shared/ui/ActionButtons'

/**
 * The modal every create / edit feature is: `Modal`, the field-less server errors above
 * the form, `Form` around the fields, Cancel and Save in the footer. What a modal fills in is
 * the fields (default slot), what sits before or after the form (`before` / `after`), and
 * the odd extra button (`actions`, in front of Cancel — or the whole `footer`).
 *
 * `submit` is emitted for both the Save button and the form's own submit (Enter), so a
 * modal wires it once, to `submit` from `useModalForm`. Dialog attributes that are not
 * props here (`append-to-body`, `close-on-click-modal`) fall through to `Modal`.
 */
withDefaults(
  defineProps<{
    modelValue?: boolean
    title?: string
    width?: string | number
    loading?: boolean
    /** Field-less server errors — `formErrors` from `useEntityForm`. */
    errors?: readonly string[]
    /** `isSubmitting` from `useModalForm` — spins Save; Save is never disabled. */
    submitting?: boolean
    /** Nothing to edit here (a page's default spot set): no form, no Save, and Cancel reads "Close". */
    readonly?: boolean
    /**
     * The record could not be read — `recordLoadError` from the feature's form hook.
     * There is then nothing to edit and nothing to save, and showing the empty form
     * would invite the publisher to write its defaults over a record nobody has seen.
     */
    loadError?: string
    /** What Save says when the action is not a save — "Pay now", "Request invoice". */
    saveLabel?: string
  }>(),
  { errors: () => [], readonly: false }
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'submit'): void
}>()
</script>

<template>
  <Modal
    :model-value="modelValue"
    :title="title"
    :width="width"
    :loading="loading"
    @close="emit('close')"
  >
    <Alert v-if="loadError" type="error" :closable="false" show-icon>
      {{ loadError }}
    </Alert>

    <template v-else>
      <slot name="before" />

      <FormErrorAlerts :errors="errors" />

      <Form v-if="!readonly" @submit="emit('submit')">
        <slot />
      </Form>

      <slot name="after" />
    </template>

    <template #footer>
      <slot name="footer">
        <slot name="actions" />
        <CancelButton @click="emit('close')">
          <template v-if="readonly" #default>Close</template>
        </CancelButton>
        <SaveButton
          v-if="!readonly && !loadError"
          :loading="submitting"
          @click="emit('submit')"
        >
          <template v-if="saveLabel" #default>{{ saveLabel }}</template>
        </SaveButton>
      </slot>
    </template>
  </Modal>
</template>
